<template>
  <article class="group py-3">
    <div class="mb-2.5 flex items-start justify-between gap-2">
      <div class="min-w-0">
        <span class="truncate text-sm font-medium text-slate-800">{{ item.title || item.todoSnapshot?.title || '--' }}</span>
        <div v-if="expectedFinishLabel" class="mt-1 flex items-center gap-1 text-xs text-slate-500">
          <CalendarDaysIcon class="h-3.5 w-3.5 shrink-0" />
          <span>预计完结：{{ expectedFinishLabel }}</span>
        </div>
      </div>
      <div class="flex shrink-0 items-center gap-0.5">
        <button
          v-if="showAnnotationButton"
          type="button"
          class="relative rounded-lg p-1.5 text-slate-400 transition-colors duration-150 hover:bg-blue-50 hover:text-blue-600"
          title="待办批注"
          aria-label="待办批注"
          @click="emit('open-annotations')"
        >
          <ChatBubbleLeftRightIcon class="h-4 w-4" />
          <span
            v-if="annotationCount > 0"
            class="absolute -right-1 -top-1 min-w-[1rem] rounded-full bg-blue-600 px-1 text-center text-[10px] font-semibold leading-4 text-white"
          >{{ annotationCount > 99 ? '99+' : annotationCount }}</span>
        </button>
        <button
          v-if="!readonly"
          type="button"
          class="rounded-lg p-1.5 text-slate-400 transition-colors duration-150 hover:bg-rose-50 hover:text-rose-500"
          title="删除工作项"
          aria-label="删除工作项"
          @click="emit('remove')"
        >
          <TrashIcon class="h-4 w-4" />
        </button>
      </div>
    </div>

    <div v-if="phase === 'plan'" class="space-y-2.5 rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50/60 to-white p-3.5">
      <div class="grid gap-2.5 md:grid-cols-2">
        <label class="group relative flex flex-col overflow-hidden rounded-lg border border-blue-100 bg-white transition-all duration-200 focus-within:border-blue-300 focus-within:ring-2 focus-within:ring-blue-100 hover:border-blue-200">
          <div class="flex items-center gap-1.5 border-b border-blue-50 px-3 py-1.5">
            <FlagIcon class="h-3.5 w-3.5 text-blue-500" />
            <span class="text-xs font-semibold text-slate-600">今日目标</span>
          </div>
          <textarea
            v-model="draft.todayGoal"
            class="flex-1 resize-none border-0 bg-transparent px-3 py-1.5 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none"
            rows="2"
            :readonly="readonly"
            placeholder="可按待办内容补充今日目标"
            @input="emitItem"
          ></textarea>
          <p v-if="errors.todayGoal" class="border-t border-red-100 bg-red-50/50 px-3 py-1 text-xs text-red-600">{{ errors.todayGoal }}</p>
        </label>
        <div class="relative flex flex-col overflow-hidden rounded-lg border border-slate-200 bg-white">
          <div class="flex items-center gap-1.5 border-b border-slate-100 px-3 py-1.5">
            <DocumentTextIcon class="h-3.5 w-3.5 text-violet-500" />
            <span class="text-xs font-semibold text-slate-600">最新批注</span>
          </div>
          <div class="min-h-[4.25rem] px-3 py-2">
            <p class="whitespace-pre-wrap break-words text-sm leading-5" :class="latestAnnotationContent ? 'text-slate-700' : 'text-slate-400'">
              {{ latestAnnotationContent || '暂无批注' }}
            </p>
            <p v-if="latestAnnotationMeta" class="mt-1 text-xs text-slate-400">{{ latestAnnotationMeta }}</p>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="space-y-2.5 rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50/60 to-white p-3.5">
      <div class="rounded-lg border border-slate-100 bg-white px-3 py-2 text-sm">
        <div class="flex items-start gap-x-4 gap-y-1">
          <FlagIcon class="mt-0.5 h-3 w-3 shrink-0 text-slate-400" />
          <span class="min-w-0 break-words font-medium text-slate-700">{{ item.todayGoal || '--' }}</span>
        </div>
        <div class="mt-1 flex items-start gap-x-4 gap-y-1">
          <SparklesIcon class="mt-0.5 h-3 w-3 shrink-0 text-slate-400" />
          <span class="min-w-0 break-words text-slate-600">{{ item.expectedOutcome || '未填写预期成果' }}</span>
        </div>
        <div v-if="isTodoBacked" class="mt-1 flex items-center gap-1 text-slate-500">
          <SignalIcon class="h-3 w-3 shrink-0" />
          <span class="font-medium text-slate-700">{{ currentTodoStatusLabel }}</span>
        </div>
      </div>

      <div class="grid gap-2.5 md:grid-cols-[minmax(0,12rem)_minmax(0,1fr)]">
        <label class="group relative flex flex-col overflow-hidden rounded-lg border border-slate-200 bg-white transition-all duration-200 focus-within:border-emerald-300 focus-within:ring-2 focus-within:ring-emerald-100 hover:border-slate-300">
          <div class="flex items-center gap-1.5 border-b border-slate-100 px-3 py-1.5">
            <CheckCircleIcon class="h-3.5 w-3.5 text-emerald-500" />
            <span class="text-xs font-semibold text-slate-600">执行结果</span>
          </div>
          <select v-model="draft.result" class="w-full border-0 bg-transparent px-3 py-1.5 text-sm text-slate-700 focus:outline-none focus:ring-0" :disabled="readonly" @change="emitItem">
            <option v-for="option in resultOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
          </select>
        </label>
        <div class="relative flex flex-col overflow-hidden rounded-lg border border-slate-200 bg-white">
          <div class="flex items-center gap-1.5 border-b border-slate-100 px-3 py-1.5">
            <DocumentTextIcon class="h-3.5 w-3.5 text-violet-500" />
            <span class="text-xs font-semibold text-slate-600">最新批注</span>
          </div>
          <div class="min-h-[4.25rem] px-3 py-2">
            <p class="whitespace-pre-wrap break-words text-sm leading-5" :class="latestAnnotationContent ? 'text-slate-700' : 'text-slate-400'">
              {{ latestAnnotationContent || '暂无批注' }}
            </p>
            <p v-if="latestAnnotationMeta" class="mt-1 text-xs text-slate-400">{{ latestAnnotationMeta }}</p>
          </div>
        </div>
      </div>

    </div>
  </article>
</template>

<script setup>
import { computed, reactive, watch } from 'vue'
import { CalendarDaysIcon, ChatBubbleLeftRightIcon, CheckCircleIcon, DocumentTextIcon, FlagIcon, SignalIcon, SparklesIcon, TrashIcon } from '@heroicons/vue/24/outline'
import { workReportTodoStatusLabel } from '../workReportUtils'

const props = defineProps({
  item: { type: Object, required: true },
  phase: { type: String, required: true },
  readonly: { type: Boolean, default: false },
  errors: { type: Object, default: () => ({}) },
  annotationCount: { type: Number, default: 0 }
})
const emit = defineEmits(['update:item', 'remove', 'open-annotations'])
const draft = reactive({})
const resultOptions = [
  { value: 'IN_PROGRESS', label: '进行中' },
  { value: 'COMPLETED', label: '已完成' }
]
const isTodoBacked = computed(() => Boolean(props.item.todoId))
const showAnnotationButton = computed(() => Boolean(props.item.id && props.item.todoId))
const currentTodoStatusLabel = computed(() => workReportTodoStatusLabel(props.item.currentTodoSnapshot?.status ?? props.item.todoSnapshot?.status))
const expectedFinishAt = computed(() => props.item.expectedFinishAt ?? props.item.currentTodoSnapshot?.dueDate ?? props.item.todoSnapshot?.dueDate)
const expectedFinishLabel = computed(() => formatDateTime(expectedFinishAt.value))
const latestAnnotationContent = computed(() => normalizeText(
  props.item.todoLatestAnnotationContent
    ?? props.item.latestTodoAnnotationContent
    ?? props.item.currentTodoSnapshot?.todoLatestAnnotationContent
    ?? props.item.todoSnapshot?.todoLatestAnnotationContent
))
const latestAnnotationCreatorName = computed(() => normalizeText(
  props.item.todoLatestAnnotationCreatorName
    ?? props.item.latestTodoAnnotationCreatorName
    ?? props.item.currentTodoSnapshot?.todoLatestAnnotationCreatorName
    ?? props.item.todoSnapshot?.todoLatestAnnotationCreatorName
))
const latestAnnotationTime = computed(() =>
  props.item.todoLatestAnnotationTime
    ?? props.item.latestTodoAnnotationTime
    ?? props.item.currentTodoSnapshot?.todoLatestAnnotationTime
    ?? props.item.todoSnapshot?.todoLatestAnnotationTime
)
const latestAnnotationMeta = computed(() => [
  latestAnnotationCreatorName.value,
  formatDateTime(latestAnnotationTime.value)
].filter(Boolean).join(' · '))

watch(() => props.item, (value) => {
  Object.keys(draft).forEach((key) => delete draft[key])
  Object.assign(draft, value || {})
  if (props.phase !== 'plan' && !isAllowedWorkItemResult(draft.result)) {
    draft.result = defaultWorkItemResult()
  }
}, { immediate: true, deep: true })

function emitItem() {
  emit('update:item', { ...draft })
}

function defaultWorkItemResult() {
  return 'IN_PROGRESS'
}

function isAllowedWorkItemResult(result) {
  return resultOptions.some((option) => option.value === result)
}

function normalizeText(value) {
  return String(value || '').trim()
}

function formatDateTime(value) {
  const text = String(value || '').trim()
  if (!text) return ''
  const normalized = text.replace('T', ' ')
  const dateTime = normalized.match(/^(\d{4}-\d{2}-\d{2})\s+(\d{2}:\d{2})/)
  if (dateTime) return `${dateTime[1]} ${dateTime[2]}`
  const dateOnly = normalized.match(/^(\d{4}-\d{2}-\d{2})/)
  return dateOnly ? dateOnly[1] : normalized
}
</script>
